import { useState } from "react";
import {
  useExpenses,
  EXPENSE_CATEGORIES,
  PAYMENT_METHODS,
  ExpenseCategory,
  PaymentMethod,
} from "@/contexts/ExpenseContext";
import { FormModal } from "@/components/form/FormModal";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CreditCard, IndianRupee, Plus, Receipt } from "lucide-react";

interface RecordExpenseDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function RecordExpenseDialog({
  isOpen,
  onOpenChange,
  onSuccess,
}: RecordExpenseDialogProps) {
  const { addExpense } = useExpenses();
  const { toast } = useToast();

  const [date, setDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [amount, setAmount] = useState<string>("");
  const [category, setCategory] = useState<ExpenseCategory>("Office");
  const [vendor, setVendor] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("Bank Transfer");
  const [description, setDescription] = useState("");
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setAmount("");
    setVendor("");
    setDescription("");
    setReference("");
    setCategory("Office");
    setPaymentMethod("Bank Transfer");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount) || 0;

    if (!vendor.trim()) {
      toast({ title: "Validation Error", description: "Vendor name is required", variant: "destructive" });
      return;
    }
    if (numAmount <= 0) {
      toast({ title: "Validation Error", description: "Expense amount must be greater than zero", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      await addExpense({
        date,
        amount: numAmount,
        category,
        vendor: vendor.trim(),
        description: description.trim(),
        paymentMethod,
        reference: reference.trim() || undefined,
      });

      toast({
        title: "Expense Recorded",
        description: `₹${numAmount.toLocaleString("en-IN")} — ${category} (${vendor.trim()})`,
      });

      resetForm();
      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      toast({ title: "Submission Error", description: err.message || "Failed to record expense", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full h-9 px-3 text-sm rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent";
  const labelCls = "text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5";

  return (
    <FormModal
      open={isOpen}
      onOpenChange={onOpenChange}
      size="standard"
      icon={<Receipt className="h-5 w-5 text-primary" />}
      title="Record Business Expense"
      description="Log direct & indirect operational expenses with instant cash/bank ledger settlement."
      footerLeft={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onOpenChange(false)}
          className="text-xs h-9"
        >
          Cancel
        </Button>
      }
      footerRight={
        <Button
          type="submit"
          form="record-expense-form"
          size="sm"
          disabled={submitting}
          className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-9 font-semibold gap-1.5 shadow-sm"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>{submitting ? "Saving..." : "Record Expense"}</span>
        </Button>
      }
    >
      <form id="record-expense-form" onSubmit={handleSubmit} className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Expense Date *</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className={labelCls}>Amount (₹) *</label>
              <input
                type="number"
                min="0.01"
                step="any"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className={inputCls}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Category *</label>
              <Select value={category} onValueChange={(v) => setCategory(v as ExpenseCategory)}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-xs">
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Payment Method *</label>
              <Select value={paymentMethod} onValueChange={(v) => setPaymentMethod(v as PaymentMethod)}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method} value={method} className="text-xs">
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Vendor / Payee *</label>
            <input
              type="text"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder="e.g. AWS Cloud, Office Supplies Co, Airtel"
              className={inputCls}
              required
            />
          </div>

          <div>
            <label className={labelCls}>Reference / Invoice No (Optional)</label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. INV-9041, UPI Ref 12345"
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>Description / Notes (Optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details about this expense..."
              className="w-full h-16 p-2 text-sm rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent resize-none"
            />
          </div>

      </form>
    </FormModal>
  );
}
